import { LivabilityData } from '@sly/core/api/types/livabilityData';
import { formatMoney, formatNumber } from '@sly/core/helpers/numbers';
import { comparePovertyRate } from '@common/data/povertyRate';
import round from 'lodash/round';
import { capitalize } from '@sly/frontend/common/helpers/utils';
import { stateNames } from '@sly/core/src/constants/geo';
import { IconName } from '@sly/icons/icon.types';

type LivabilityDataContent = {
  heading: string;
  subheading?: string;
  Icon?: IconName;
  tooltip?: string;
};

export type DemographicsData = Record<
  'population' | 'medianIncome' | 'diversity',
  LivabilityDataContent
>;

export type NeighborhoodData = Record<
  'crimeRate' | 'nearbyAccessStores' | 'nearbyAccessParks' | 'housing',
  LivabilityDataContent
>;

export type HealthData = Record<
  | 'lifeExpectancy'
  | 'hospitalizationPrevention'
  | 'airQuality'
  | 'waterQuality',
  LivabilityDataContent
>;

function removeItemOnce(arr: string[], value: string) {
  const index = arr.indexOf(value);
  if (index > -1) {
    arr.splice(index, 1);
  }
  return arr;
}

const fields = {
  demographics: ['population', 'medianIncome', 'diversity'],
  neighborhood: [
    'housing',
    'nearbyAccessStores',
    'nearbyAccessParks',

    'crimeRate'
  ],
  health: [
    'lifeExpectancy',
    'hospitalizationPrevention',
    'airQuality',
    'waterQuality'
  ]
};

const diversityToLabelMap = {
  hispanic: 'Hispanic',
  white: 'White',
  afam: 'Black',
  asian: 'Asian'
};

export const parseNeighborhoodSurveyData = (
  livabilityData: LivabilityData,
  state: keyof typeof stateNames
) => {
  const info = livabilityData?.info || {};
  const demographics = info?.demographics || {};
  const metrics = info?.metrics || {};

  const demographicsdData = fields.demographics.reduce((acc, key) => {
    // todo: population data
    const totalPopulation = demographics?.totalPopulation;
    if (key === 'population' && totalPopulation) {
      acc[key] = {
        heading: `Total population: ${formatNumber(totalPopulation)}`
      };
      let populationString = '';
      const fiftyPlus = demographics?.fiftyPlus;
      const sixtyFivePlus = demographics?.sixtyFivePlus;
      if (fiftyPlus) {
        populationString += `${fiftyPlus}% of population is 50+ years old`;
        if (sixtyFivePlus) {
          populationString += ', ';
        }
      }
      if (sixtyFivePlus) {
        populationString += `${sixtyFivePlus}% is 65+ years old`;
      }
      if (populationString) {
        acc[key].subheading = populationString;
      }
    }

    if (key === 'medianIncome' && demographics?.medianIncome) {
      acc[key] = {
        heading: `Median income: ${formatMoney(demographics?.medianIncome)}`
      };
      if (demographics?.poverty) {
        acc[
          key
        ].tooltip = `<p style="margin-bottom:8px">Census Bureau uses a set of money income thresholds that vary by family size and composition to determine who is in poverty. If a family's total income is less than the family's threshold, then that family and every individual in it is considered in poverty.</p> <a style="text-decoration:underline" href="https://www.census.gov/topics/income-poverty/poverty/guidance/poverty-measures.html" target="_blank">Learn more here</a>`;

        // FIX TYPES: state type should be StateAbbr
        const subheading = comparePovertyRate(state, demographics?.poverty);
        if (subheading) {
          acc[key].subheading = subheading;
        }
      }
    }

    if (key === 'diversity') {
      const diversityArr = ['asian', 'hispanic', 'white', 'afam'];
      const subheading = Object.entries(demographics).reduce(
        (acc, [key, value]) => {
          if (diversityArr.includes(key)) {
            const race =
              diversityToLabelMap?.[key as keyof typeof diversityToLabelMap] ||
              capitalize(key);
            removeItemOnce(diversityArr, key);
            if (!acc) {
              acc = `${value}% of population identify themselves as ${race}`;
            } else {
              if (diversityArr.length === 0) {
                acc += `, and ${value}% as ${race}`;
              } else {
                acc += `, ${value}% as ${race}`;
              }
            }
          }

          return acc;
        },
        ''
      );

      if (subheading) {
        acc[key] = {
          heading: 'Diversity'
        };
        acc[key].subheading = subheading;
      }
    }

    return acc;
  }, {} as DemographicsData);

  const neighborhoodData = fields.neighborhood.reduce((acc, key) => {
    if (key === 'crimeRate' && metrics.crimeRate) {
      acc[key] = {
        heading: `Crime rate: ${round(
          metrics.crimeRate
        )} crimes per 10,000 people`,
        Icon: 'HomeSmallStroke'
      };
    }

    if (key === 'nearbyAccessStores') {
      const marketProximity = metrics?.marketProximity;
      if (marketProximity) {
        const pluralOrSingular = marketProximity >= 2 ? 'stores' : 'store';
        acc[key] = {
          heading: `Nearby access to ${round(
            marketProximity
          )} grocery ${pluralOrSingular} and farmers market`,
          Icon: 'HomeSmallStroke'
        };
      }
    }

    if (key === 'nearbyAccessParks') {
      const parkProximity = metrics?.parkProximity;
      const pluralOrSingular = parkProximity >= 2 ? 'parks' : 'park';
      if (parkProximity) {
        acc[key] = {
          heading: `Nearby access to ${round(
            parkProximity
          )} ${pluralOrSingular}`,
          Icon: 'HomeSmallStroke'
        };
      }
    }

    if (key === 'housing') {
      const housingCost = metrics?.housingCost;
      if (housingCost) {
        acc[key] = {
          heading: `Average housing cost: ${formatMoney(
            round(housingCost)
          )} per month`,
          Icon: 'HomeSmallStroke'
        };
      }
    }

    return acc;
  }, {} as NeighborhoodData);

  const healthData = fields.health.reduce((acc, key) => {
    if (key === 'lifeExpectancy' && demographics?.lifeExpectancy) {
      acc[key] = {
        heading: `Life expectancy: ${round(
          demographics.lifeExpectancy
        )} years old`,
        Icon: 'Care'
      };
    }

    if (
      key === 'hospitalizationPrevention' &&
      metrics?.hospitalizationPrevention
    ) {
      acc[key] = {
        heading: `Preventable hospitalizations: ${round(
          metrics.hospitalizationPrevention,
          1
        )} per 1,000 patients`,
        Icon: 'Care'
      };
    }

    const airQuality = metrics?.airQuality;
    const waterQuality = metrics?.waterQuality;

    if (
      key === 'airQuality' &&
      airQuality !== null &&
      airQuality !== undefined
    ) {
      acc[key] = {
        heading: `Regional air quality: ${round(
          airQuality,
          1
        )} unhealthy air quality days per year`,
        Icon: 'Care'
      };
    }

    if (
      key === 'waterQuality' &&
      waterQuality !== null &&
      waterQuality !== undefined
    ) {
      acc[key] = {
        heading: `Drinking water quality: ${round(
          waterQuality,
          1
        )}% of people are exposed to violations`,
        Icon: 'Care'
      };
    }

    return acc;
  }, {} as HealthData);

  return {
    demographics: demographicsdData,
    neighborhood: neighborhoodData,
    health: healthData
  };
};
